import React, { useCallback, useMemo } from 'react';
import { divide, get, isEmpty, noop, sum } from 'lodash';
import { GridColDef } from '@mui/x-data-grid';
import Form from 'mobx-react-form';
import { observer } from 'mobx-react-lite';

import {
  DEFAULT_EXPENSES_KEYS,
  EXPENSE_COMPARABLES_INFO,
  EXPENSE_RATIO_ROW,
  TOTAL_OPERATING_EXPENSES,
  TOTAL_OPERATING_EXPENSES_CATEGORIES,
  TOTAL_OPERATING_EXPENSES_PER_SF,
  TOTAL_OPERATING_EXPENSES_PER_UNIT,
} from 'shared/constants/expenses';

import { ExpenseCategory, GridRow } from '../types';
import { getValueFormatter, getValuePerBasis } from '../helpers';

import ColumnHeader from './ColumnHeader';
import { formatCurrencyFloat, formatCurrencyInt } from 'shared/utils/numberFormatters';
import { ExpenseComp } from 'types/underwriting/income-approach/expenses';
import { Table } from 'components/shared/Table';

interface Props {
  form?: Form;
  propertyType?: string;
  deleteColumn?: (compId: string) => void;
  basis?: string;
  expenseCategories: ExpenseCategory[];
  latestCompVersions?: Record<string, number>;
  disabled?: boolean;
  expenseComparables?: ExpenseComp[];
}

const ComparableExpensesTable: React.FC<Props> = observer(
  ({
    form,
    basis = 'Gross',
    expenseCategories = [],
    deleteColumn,
    latestCompVersions,
    disabled,
    expenseComparables,
  }) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const expenseComps: ExpenseComp[] = disabled
      ? expenseComparables
      : form?.$('incomeApproach.expenses.expenseComps').value || [];

    const valueFormatter = useCallback(
      (value: any, row: GridRow, column: any) => {
        const { id } = row;
        const protectedCategories = [...EXPENSE_COMPARABLES_INFO, ...TOTAL_OPERATING_EXPENSES_CATEGORIES];

        const formatter = getValueFormatter(row.id, basis);

        if (protectedCategories.includes(id)) {
          return formatter(value);
        }

        const valuePerBasis = getValuePerBasis(basis, value, column);

        const finalValue = formatter(valuePerBasis);

        return finalValue;
      },
      [basis]
    );

    const columns: GridColDef[] = useMemo(() => {
      const cols: GridColDef[] = [
        {
          field: 'name',
          headerName: '',
          width: 250,
          flex: 1.5,
        },
      ];

      expenseComps.forEach((item, index) => {
        const latestCompVersion = latestCompVersions ? latestCompVersions[item.boweryId || ''] : null;

        let isOutdated = false;
        if (latestCompVersion) {
          isOutdated = item.version !== latestCompVersion;
        }
        cols.push({
          field: item.key,
          headerName: '',
          width: 200,
          flex: 1,
          sortable: false,
          disableColumnMenu: true,
          editable: false,
          headerAlign: 'center',
          renderHeader: () => (
            <ColumnHeader
              disabled
              item={item}
              index={index}
              deleteColumn={deleteColumn || noop}
              isOutdated={isOutdated}
            />
          ),
          valueFormatter: (value, row) => {
            return valueFormatter(value, row, item);
          },
          type: 'number',
        });
      });
      const endCols: GridColDef[] = [
        {
          field: 'average',
          headerName: 'Average',
          width: 100,
          type: 'number',
          editable: false,
          sortable: false,
          valueFormatter: (value, row) => {
            if (row.id === 'expensesTotalGross.psf') {
              return formatCurrencyFloat(value);
            }
            return formatCurrencyInt(value);
          },
        },
      ];

      return [...cols, ...endCols];
    }, [expenseComps, latestCompVersions, deleteColumn, valueFormatter]);

    const rows: GridRow[] = useMemo(() => {
      const expenseRows: GridRow[] = expenseCategories.map((expenseCategory) => {
        const rowData: GridRow = {
          id: expenseCategory.key,
          name: expenseCategory.category,
          basisValue: basis,
        };
        const compValues: number[] = [];

        expenseComps.forEach((item: ExpenseComp) => {
          if (Object.values(DEFAULT_EXPENSES_KEYS).includes(expenseCategory.key)) {
            const category: string = expenseCategory.key;
            const expenses = item.expenses as unknown as Record<string, { total?: number; value?: number }> | undefined;
            const expense = expenses?.[category] || {};
            const value = expense.total ?? expense.value ?? 0;
            rowData[item.key] = value;
            compValues.push(value);
          } else {
            const category: string = expenseCategory.key;
            const expenses = item.expenses as unknown as Record<string, { value?: number }> | undefined;
            const value =
              // Use explicit type for item to avoid 'any' index error
              (item as Record<string, any>)[expenseCategory.key] ?? expenses?.[category]?.value ?? 0;
            rowData[item.key] = value;
          }
        });
        rowData.average = !isEmpty(compValues) ? divide(sum(compValues), compValues.length) : null;
        return rowData;
      });
      const totalRows = [TOTAL_OPERATING_EXPENSES, TOTAL_OPERATING_EXPENSES_PER_SF, TOTAL_OPERATING_EXPENSES_PER_UNIT];
      const summaryRows: GridRow[] = [EXPENSE_RATIO_ROW, ...totalRows].map((row) => {
        const rowData: GridRow = {
          id: row.key,
          name: row.name,
          basisValue: basis,
          isTotal: true,
        };
        const compValues: number[] = [];
        expenseComps.forEach((item) => {
          rowData[item.key] = get(item, row.key) || 0;
          compValues.push(get(item, row.key) || 0);
        });
        rowData.average =
          isEmpty(compValues) || row.key === 'expenseRatio' ? null : divide(sum(compValues), compValues.length);
        return rowData;
      });

      const allRows = [...expenseRows, ...summaryRows];
      return allRows;
    }, [expenseCategories, basis, expenseComps]);

    return (
      <div style={{ height: '100%', width: '100%' }}>
        <Table
          key={`datagrid-${basis}-${expenseComps.length}`}
          rows={rows}
          columns={columns}
          getRowClassName={(params) => (params.row.isTotal ? 'total-row' : 'disabled-cell')}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              borderRight: '1px solid rgba(224, 224, 224, 1)',
            },
            '& .total-row': {
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
              borderTop: '1px solid #ddd',
            },
            '& .MuiDataGrid-cell': {
              borderRight: '1px solid #f0f0f0',
            },
            '& .disabled-cell': {
              backgroundColor: 'rgb(245, 245, 245)',
              color: '#666',
              fontWeight: 'normal',
            },
          }}
        />
      </div>
    );
  }
);

export default ComparableExpensesTable;
